import React from 'react';
import { Box } from '@mui/material';
import { mixWithWhite, findDrillDown } from './regulationConfig';
import { trakrefBlue } from '../../color_utils';

// One vertical column per in-scope regulation: total on top, a full-height bar
// split proportionally into Needs attention (lighter shade, top) and Compliant
// (full-strength regulation color, bottom), then the name and scope label.
// AB#17128 AC14: a regulation with a drill-down opens it on click; the rest stay display-only.
const segmentSx = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    minHeight: '24px',
};

const RegulationColumns = ({ regulations, onSelect }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${regulations.length}, minmax(0, 16.66%))`,
            justifyContent: 'center',
            gap: '8px',
            pt: '4px',
        }}
    >
        {regulations.map((reg) => {
            const needsAttention = reg.needsAttentionCount;
            const compliant = reg.assetCount - reg.needsAttentionCount;
            const clickable = Boolean(onSelect && findDrillDown(reg.key));
            return (
                <Box
                    key={reg.key}
                    {...(clickable && {
                        role: 'link',
                        tabIndex: 0,
                        'aria-label': `Open ${reg.name}`,
                        onClick: () => onSelect(reg),
                        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(reg); } },
                    })}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: '12px 8px 6px',
                        borderRadius: '8px',
                        ...(clickable && {
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#F5F7FD' },
                            // Keyboard focus gets a real outline, not just the hover tint (N5)
                            '&:focus-visible': { backgroundColor: '#F5F7FD', outline: `2px solid ${trakrefBlue}`, outlineOffset: '2px' },
                        }),
                    }}
                >
                    <Box sx={{ fontSize: '15px', color: 'text.secondary', textAlign: 'center', mb: '6px' }}>
                        {reg.assetCount.toLocaleString()}
                    </Box>
                    <Box sx={{ width: '100%', height: '240px', display: 'flex', alignItems: 'flex-end', mb: '14px' }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                maxWidth: '64px',
                                m: '0 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                backgroundColor: '#EBEDF8',
                            }}
                        >
                            {needsAttention > 0 && (
                                <Box
                                    title={`${needsAttention}`}
                                    sx={{
                                        ...segmentSx,
                                        flexGrow: needsAttention,
                                        backgroundColor: mixWithWhite(reg.color, 0.22),
                                        color: '#000',
                                    }}
                                >
                                    {needsAttention.toLocaleString()}
                                </Box>
                            )}
                            {compliant > 0 && (
                                <Box
                                    title={`${compliant}`}
                                    sx={{
                                        ...segmentSx,
                                        flexGrow: compliant,
                                        backgroundColor: reg.color,
                                        color: '#fff',
                                    }}
                                >
                                    {compliant.toLocaleString()}
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', textAlign: 'center' }}>
                        {reg.name}
                    </Box>
                    <Box sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        {reg.scopeLabel}
                    </Box>
                </Box>
            );
        })}
    </Box>
);

export default RegulationColumns;
